import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Layout, { DateInput } from "./Layout";
import { formatDate, getApiUrl, getTodayIso } from "./config";

function ExpensesReport() {
  const today = getTodayIso();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [rows, setRows] = useState([]);

  const getModeLabel = (row) => {
    const cash = Number(row?.cash_amount) || 0;
    const upi = Number(row?.upi_amount) || 0;
    const bank = Number(row?.bank_amount) || 0;
    const flags = [cash > 0, upi > 0, bank > 0].filter(Boolean).length;
    if (flags === 0) return "-";
    if (flags === 1) {
      if (cash > 0) return "Cash";
      if (upi > 0) return "UPI";
      return "Bank Transfer";
    }
    return "Mixed";
  };

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiUrl()}/accounts`, { params: { type: "expense" } });
      setAccounts(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      const params = { from, to };
      if (accountId) params.account_id = accountId;
      const res = await axios.get(`${getApiUrl()}/reports/expenses`, { params });
      setRows(res.data || []);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to load expenses";
      alert(message);
    }
  }, [from, to, accountId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const totals = useMemo(() => {
    const sum = (key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    return {
      cash: sum("cash_amount"),
      upi: sum("upi_amount"),
      bank: sum("bank_amount"),
      total: sum("total_amount")
    };
  }, [rows]);

  const handleExportPdf = () => {
    window.print();
  };

  return (
    <Layout>
      <div className="container mt-4">
        <h2>Expenses Report</h2>

        <div className="card p-4 mb-4 no-print">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-bold">From</label>
              <DateInput value={from} onChange={setFrom} />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">To</label>
              <DateInput value={to} onChange={setTo} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Account</label>
              <select className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">All</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-grid">
              <button type="button" className="btn btn-primary" onClick={fetchRows}>
                Refresh
              </button>
            </div>
            <div className="col-md-2 d-grid">
              <button type="button" className="btn btn-outline-dark" onClick={handleExportPdf}>
                Export PDF
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Date</th>
                <th>Account</th>
                <th>Purpose</th>
                <th>Mode</th>
                <th className="text-end">Cash</th>
                <th className="text-end">UPI</th>
                <th className="text-end">Bank</th>
                <th className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.account_name}</td>
                  <td>{r.purpose || "-"}</td>
                  <td>{getModeLabel(r)}</td>
                  <td className="text-end">₹ {(Number(r.cash_amount) || 0).toFixed(2)}</td>
                  <td className="text-end">₹ {(Number(r.upi_amount) || 0).toFixed(2)}</td>
                  <td className="text-end">₹ {(Number(r.bank_amount) || 0).toFixed(2)}</td>
                  <td className="text-end fw-bold">₹ {(Number(r.total_amount) || 0).toFixed(2)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    No expenses
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="4" className="text-end">
                  Total
                </th>
                <th className="text-end">₹ {totals.cash.toFixed(2)}</th>
                <th className="text-end">₹ {totals.upi.toFixed(2)}</th>
                <th className="text-end">₹ {totals.bank.toFixed(2)}</th>
                <th className="text-end">₹ {totals.total.toFixed(2)}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default ExpensesReport;
